import {exists} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {useEffect, useRef, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {
  inputElementBorderRadius,
  inputElementHeight,
  inputElementPadding,
} from '../../../constants/input';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../Buttons/UnstyledButton';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

const labelTransitionTime = '100ms';

type ActiveStyle = {
  backgroundColor: string;
  backgroundColorFocused?: string;
  borderColor: string;
};
export interface Props
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  /**
   * Defines how the text input appears when active (=selected and/or filled).
   * Allows to easily create different appearances, e.g. simple text input vs. material style
   */
  activeStyle: ActiveStyle;
  componentLeft?: {
    Component: React.ComponentType<React.PropsWithChildren>;
    width: number;
  };
  componentRight?: {
    Component: React.ComponentType<React.PropsWithChildren>;
    width: number;
  };
  className?: string;
  label?: string;
  value?: string | number | readonly string[];
  errorMessage?: string;
  onPressEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  // The input value attribute will be cleared (set to empty string) after the onBlur / onPressEnter event
  forceEmptyValue?: boolean;
  // This will exclude dot and comma characters as well
  acceptOnlyNumerics?: boolean;
  dataTestId?: string;
  passwordLabels?: {
    showPassword: string;
    hidePassword: string;
  };
  hideErrorIcon?: boolean;
}

export const TextInput = ({
  activeStyle,
  componentLeft,
  componentRight,
  label,
  placeholder,
  type: propsType,
  errorMessage,
  className,
  value: propsValue,
  forceEmptyValue,
  acceptOnlyNumerics,
  onPressEnter,
  dataTestId,
  passwordLabels,
  hideErrorIcon,
  onFocus,
  ref,
  ...props
}: Props & {ref?: React.Ref<HTMLInputElement>}) => {
  const offsetLeft = componentLeft
    ? componentLeft.width + inputElementPadding
    : inputElementPadding;
  const offsetRight = componentRight
    ? componentRight.width + inputElementPadding
    : inputElementPadding;

  const hasError = exists(errorMessage);
  const labelRef = useRef<HTMLLabelElement>(null);

  const [value, setValue] = useState<Props['value']>(propsValue);
  const [showPassword, setShowPassword] = useState(false);
  const [type, setType] = useState(propsType);
  const [inputFocused, setInputFocused] = useState(false);

  const setCurrentValue = (value: Props['value']) => {
    setValue(value);
    // This is a workaround to properly set the data attribute.
    // Setting it directly on the element would result in the wrong value, as this component is typically managed by a react-form, but we're still controlling it internally.
    labelRef.current?.setAttribute('data-has-value', `${Boolean(value)}`);
  };

  useEffect(() => {
    if (exists(propsValue)) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setCurrentValue(propsValue);
    }
  }, [propsValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(e);
    setCurrentValue(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    props.onBlur?.(e);
    setInputFocused(false);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setInputFocused(true);
    onFocus?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onPressEnter?.(e);
    }

    if (
      acceptOnlyNumerics &&
      !/[0-9]/.test(e.key) &&
      !e.metaKey &&
      ![
        'Tab',
        'ArrowUp',
        'ArrowDown',
        'ArrowRight',
        'ArrowLeft',
        'Backspace',
        'Delete',
      ].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  const renderPasswordToggle = () => {
    const handlePasswordToggle = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      e.preventDefault();
      e.stopPropagation();
      setShowPassword(!showPassword);
      setType(showPassword ? 'password' : 'text');
      // setFocusAtTextEnding();
    };

    // only render "showPassword" once we actually have a value and we're inside a password type input
    if (!props.disabled && propsType === 'password' && value) {
      return (
        <PasswordToggle
          data-testid="PasswordToggle"
          onClick={handlePasswordToggle}
          aria-label={undefined}
        >
          {showPassword
            ? passwordLabels?.hidePassword
            : passwordLabels?.showPassword}
        </PasswordToggle>
      );
    }

    return null;
  };

  return (
    <Wrapper data-testid="textInputWrapper" className={className}>
      <label
        data-testid="textInputLabel"
        data-focused={inputFocused || propsType === 'date'}
        data-has-error={hasError}
        ref={labelRef}
      >
        <StyledInput
          {...props}
          data-testid={dataTestId ?? 'textInput'}
          activeStyle={activeStyle}
          offsetLeft={offsetLeft}
          offsetRight={offsetRight}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          hasError={hasError}
          placeholder={' '}
          ref={ref}
          type={type}
          value={forceEmptyValue ? undefined : value ?? ''}
        />

        <LabelText
          data-testid="textInputLabelText"
          offsetLeft={offsetLeft}
          placeholderAvailable={Boolean(placeholder)}
          activeStyle={activeStyle}
        >
          {label}
        </LabelText>
        {Boolean(placeholder) && (!value || forceEmptyValue) && (
          <PlaceholderText
            data-testid={
              dataTestId ? `${dataTestId}Placeholder` : 'textInputPlaceholder'
            }
            offsetLeft={offsetLeft}
          >
            {placeholder}
          </PlaceholderText>
        )}
        {renderPasswordToggle()}
        {componentLeft && (
          <ComponentWrapper
            data-testid="textInputComponentLeftWrapper"
            width={componentLeft?.width}
          >
            <componentLeft.Component />
          </ComponentWrapper>
        )}

        {componentRight && (
          <ComponentWrapper
            data-testid="textInputComponentRightWrapper"
            right
            width={componentRight?.width}
          >
            <componentRight.Component />
          </ComponentWrapper>
        )}
        {hasError && !hideErrorIcon && (
          <>
            <ErrorIcon
              data-testid="ErrorIcon"
              asset={GenericIconName.Error}
              size="S"
            />
            <InfoText data-testid="ErrorInfoText">{errorMessage}</InfoText>
          </>
        )}
      </label>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  flex-grow: 1;
`;

const PlaceholderText = styled.span<{offsetLeft: number}>`
  position: absolute;
  left: 0;
  transform: ${({offsetLeft}) => `translate(${offsetLeft}px, 15px)`};
  pointer-events: none;
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Grey1};
  padding-left: 2px;
  padding-right: 2px;
  transition: opacity ${labelTransitionTime};

  [data-focused='true'][data-has-value='true'] & {
    opacity: 0;
  }
`;

const LabelText = styled.span<{
  offsetLeft: number;
  placeholderAvailable: boolean;
  activeStyle: ActiveStyle;
}>`
  position: absolute;
  left: 0;
  transform: ${({offsetLeft}) => `translate(${offsetLeft}px, 15px)`};
  pointer-events: none;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Grey1};
  transition: transform ${labelTransitionTime}, color ${labelTransitionTime},
    font-size ${labelTransitionTime}, opacity ${labelTransitionTime};
  padding-left: 4px;
  padding-right: 4px;
  opacity: ${props => (props.placeholderAvailable ? 0 : 1)};

  [data-focused='true'] &,
  [data-has-value='true'] & {
    transform: ${({offsetLeft}) => `translate(${offsetLeft}px, -5px)`};
    background-color: ${({activeStyle}) =>
      activeStyle.backgroundColorFocused ?? activeStyle.backgroundColor};
    font-size: ${FONT_SIZES.px10};
    color: ${COLORS.Black};
    opacity: 1;
  }

  [data-has-error='true'][data-has-value='true'] & {
    color: ${COLORS.Red};
  }
`;

const ComponentWrapper = styled.div<{right?: boolean; width: number}>`
  position: absolute;
  top: 1px;
  left: ${({right}) => (right ? 'initial' : '1px')};
  right: ${({right}) => (right ? '1px' : 'initial')};
  width: ${({width}) => `${width}px`};
  height: ${inputElementHeight}px;
  background-color: transparent;
  box-sizing: border-box;
`;

const StyledInput = styled.input<{
  activeStyle: ActiveStyle;
  offsetLeft: number;
  offsetRight: number;
  hasError: boolean;
}>`
  width: 100%;
  height: ${inputElementHeight}px;
  border-radius: ${inputElementBorderRadius}px;
  background-color: ${COLORS.Grey4};
  border: 1px solid ${COLORS.Grey4};
  padding-top: ${inputElementPadding}px;
  padding-bottom: ${inputElementPadding}px;
  padding-right: ${({offsetRight}) => `${offsetRight}px`};
  padding-left: ${({offsetLeft}) => `${offsetLeft}px`};
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  box-sizing: border-box;

  /* Disable the default spinner buttons of numeric input */
  &[type='number'] {
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &:not(:-ms-input-placeholder) {
    border: ${({activeStyle}) => `1px solid ${activeStyle.borderColor}`};
    background-color: ${({activeStyle}) => activeStyle.backgroundColor};
  }

  &:not(:placeholder-shown) {
    border: ${({activeStyle}) => `1px solid ${activeStyle.borderColor}`};
    background-color: ${({activeStyle}) => activeStyle.backgroundColor};
  }

  &:active {
    border: ${({activeStyle}) => `1px solid ${activeStyle.borderColor}`};
    background-color: ${({activeStyle}) =>
      activeStyle.backgroundColorFocused ?? activeStyle.backgroundColor};
  }

  &:focus {
    border: ${({activeStyle}) => `1px solid ${activeStyle.borderColor}`};
    background-color: ${({activeStyle}) =>
      activeStyle.backgroundColorFocused ?? activeStyle.backgroundColor};
    outline: none;
  }

  &:hover {
    border: ${({activeStyle}) => `1px solid ${activeStyle.borderColor}`};
  }

  [data-has-value='true'] & {
    background-color: ${({activeStyle}) =>
      activeStyle.backgroundColorFocused ?? activeStyle.backgroundColor};
  }

  [data-has-error='true'] & {
    border: 1px solid ${COLORS['Alerts.Error.Primary']};

    &:hover {
      border: 1px solid ${COLORS['Alerts.Error.Primary']};
    }
  }
`;

const InfoText = styled.span`
  display: block;
  margin-left: 15px;
  margin-right: 15px;
  margin-top: 5px;
  max-width: calc(100% - 30px);
  font-size: ${FONT_SIZES.px10};
  line-height: 1.4;
  color: ${COLORS.Red};
  font-weight: ${FONT_WEIGHTS.bold};
`;

const ErrorIcon = styled(Icon)`
  position: absolute;
  top: 11px;
  right: 8px;
  fill: ${COLORS.Red};
`;

const PasswordToggle = styled(UnstyledButton)`
  color: ${COLORS.Grey2};
  font-size: ${FONT_SIZES.px10};
  font-weight: bold;
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 1;
  cursor: default;
  padding: 0.2rem;
  cursor: pointer;

  &:hover {
    color: ${COLORS.Grey1};
  }
`;
